"use client"

import * as React from "react"
import Link from "next/link"
import { MenuIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { Wordmark } from "@/components/site/wordmark"
import { NAV_LINKS } from "@/components/site/nav-links"

function SiteHeader() {
  const [scrolled, setScrolled] = React.useState(false)
  const [open, setOpen] = React.useState(false)

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 flex h-[68px] items-center border-b transition-[background-color,border-color,backdrop-filter] duration-300",
        // Transparente au repos, opaque dès qu'on défile ou que le panneau est ouvert.
        scrolled || open
          ? "border-border bg-background/85 backdrop-blur-lg"
          : "border-transparent bg-transparent"
      )}
    >
      <div className="mx-auto flex w-full max-w-brand items-center gap-8 px-gutter">
        <Link href="/" aria-label="Accueil Cairn" className="lg:mr-0 mr-auto">
          <Wordmark />
        </Link>

        <nav
          aria-label="Navigation principale"
          className="mr-auto hidden gap-[30px] font-heading text-[0.8125rem] tracking-brand uppercase lg:flex"
        >
          {NAV_LINKS.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          <Button
            variant="ghost"
            size="brand-sm"
            className="hidden sm:inline-flex"
            render={<Link href="/mon-espace" />}
          >
            Mon espace
          </Button>
          <Button
            size="brand-sm"
            className="hidden min-[481px]:inline-flex"
            render={<Link href="/demarrer" />}
          >
            Commencer
          </Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden"
                  aria-label="Ouvrir le menu"
                >
                  <MenuIcon />
                </Button>
              }
            />
            {/* Pleine largeur : l'original couvrait tout l'écran, pas un tiroir latéral. */}
            <SheetContent
              side="right"
              className="gap-7 border-l-0 px-gutter pt-8 pb-12 data-[side=right]:w-full data-[side=right]:sm:max-w-full"
            >
              <SheetTitle className="sr-only">Navigation</SheetTitle>

              <nav
                aria-label="Navigation mobile"
                className="flex flex-col font-heading text-2xl tracking-[0.04em] uppercase"
              >
                {[...NAV_LINKS, { href: "/mon-espace", label: "Mon espace" }].map(
                  (link) => (
                    <SheetClose
                      key={link.label}
                      render={
                        <Link
                          href={link.href}
                          className="border-b border-border py-[18px]"
                        >
                          {link.label}
                        </Link>
                      }
                    />
                  )
                )}
              </nav>

              <SheetClose
                render={
                  <Button size="brand" className="w-full" render={<Link href="/demarrer" />}>
                    Trouver mon rythme
                  </Button>
                }
              />
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  )
}

export { SiteHeader }
